import { getLocales } from 'assets/locales/Locale';
import { usePublicCreateClaimProvider } from '../Hooks';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';

type ReviewRow = {
  label: React.ReactNode;
  value?: React.ReactNode;
};

type ReviewSectionProps = {
  title: React.ReactNode;
  rows: ReviewRow[];
};

function ReviewSection({ title, rows }: ReviewSectionProps) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3>{title}</h3>
      <div>
        {rows.map((row, index) => (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr',
              gap: 16,
              padding: '6px 0',
              borderBottom: '1px solid #edf0f5',
              fontSize: 13,
            }}
          >
            <span>{row.label}:</span>
            <strong>{row.value || 'N/A'}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
export default function PublicCreateClaimReview() {
  const { lang } = getLocales();
  const publicCreateClaim = usePublicCreateClaimProvider();
  const data = publicCreateClaim.form.data;
  const contract = data.claim?.contract;
  const dealer = data.claim?.contract?.dealer;
  const vehicle = data.claim?.contract?.vehicle;
  return (
    <>
      <h2>{lang.publicClaimReview}</h2>

      <p>{lang.publicClaimReviewParagraph}.</p>

      <ReviewSection
        title={lang.dealer}
        rows={[
          { label: lang.name, value: dealer?.name },
          { label: lang.primaryEmailAddress, value: dealer?.primaryEmailAddress },
          { label: lang.phoneNumber, value: dealer?.primaryPhoneNumber },
          { label: lang.streetAddress, value: dealer?.streetAddress },
          { label: lang.city, value: dealer?.city },
          { label: lang.postalCode, value: dealer?.postalCode },
          { label: lang.provinceCode, value: dealer?.provinceCode },
          { label: lang.countryCode, value: dealer?.countryCode },
        ]}
      />

      <ReviewSection
        title={lang.vehicleInfo}
        rows={[
          { label: lang.vin, value: vehicle?.vin },
          { label: lang.year, value: vehicle?.year },
          { label: lang.makeCode, value: vehicle?.makeCode },
          { label: lang.model, value: vehicle?.model },
          { label: lang.odometerReading, value: `${vehicle?.posOdometerReading}km` },
          { label: lang.stockNumber, value: vehicle?.stockNumber },
          { label: lang.purchaseType, value: vehicle?.purchaseType },
          {
            label: lang.purchasePrice,
            value: vehicle?.purchasePrice
              ? `$${formatNumberWithSeparator(vehicle.purchasePrice.toFixed(2))}`
              : undefined,
          },
          {
            label: lang.financedAmount,
            value: vehicle?.financedAmount
              ? `$${formatNumberWithSeparator(vehicle.financedAmount.toFixed(2))}`
              : undefined,
          },
        ]}
      />

      <ReviewSection
        title={lang.contract}
        rows={[
          { label: lang.name, value: contract?.appNumber },
          { label: lang.termInMonths, value: contract?.termInMonths },
          { label: lang.effectiveDate, value: localizeDate(contract?.effectiveDate, defaultDateFormat) },
          { label: lang.expiryDate, value: localizeDate(contract?.expiryDate, defaultDateFormat) },
          { label: lang.warrantyOption, value: contract?.warrantyOption },
          { label: lang.class, value: contract?.class },
          { label: lang.maxKm, value: contract?.maxKm },
          { label: lang.coverageKm, value: contract?.coverageKm },
          {
            label: lang.totalPrice,
            value: contract?.surchargesPriceCAD
              ? `$${formatNumberWithSeparator(contract.surchargesPriceCAD.toFixed(2))}`
              : undefined,
          },
          {
            label: lang.surcharges,
            value: contract?.surchargesPriceCAD
              ? `$${formatNumberWithSeparator(contract.surchargesPriceCAD.toFixed(2))}`
              : undefined,
          },
        ]}
      />
    </>
  );
}
